import { useCallback, useEffect, useState } from "react";
import { localStorageAsync } from "#app/lib/local-storage";
import {
  Box,
  Button,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  Icon,
  Sheet,
  SheetClose,
  SheetPanel,
  SheetPopup,
  SheetTitle,
  Text,
  ToggleGroup,
  ToggleGroupItem,
  useIsMobile,
} from "@salestrak/ui";

import type { FilterConfig, UseFiltersResult } from "#app/lib/filters/types";
import {
  ANCHORED_SHEET_POPUP_CLASS,
  useContentAnchorInsets,
} from "#app/lib/hooks/use-anchor-insets";
import { useCloseOnNavigate } from "#app/lib/hooks/use-close-on-navigate";
import { QuickFilters } from "./quick-filters";
import { AdvancedFilters } from "./advanced-filters";
import { FilterCountBadge } from "./filter-count-badge";

type FilterPanelProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: FilterConfig;
  filters: UseFiltersResult;
};

type FilterTab = "simple" | "advanced";

const MOBILE_SNAP_POINTS = [0.92];

/**
 * The filter panel: a right-side `Sheet` on desktop, a `Drawer` on mobile.
 * Tab preference is persisted per entity category via localStorage.
 *
 * The desktop sheet is non-blocking: `modal={false}` + `showBackdrop={false}`,
 * with `disablePointerDismissal` so a click outside leaves it open. Its actions
 * are an in-flow header cluster with the stock close button switched off. Like
 * every side panel, it insets within the content card (useContentAnchorInsets)
 * rather than spanning the viewport.
 */
export function FilterPanel({
  open,
  onOpenChange,
  config,
  filters,
}: FilterPanelProps) {
  const isMobile = useIsMobile();
  const insets = useContentAnchorInsets(open && !isMobile);
  const [activeTab, setActiveTab] = useState<FilterTab>("simple");

  // Dialog-system default: click-behind can navigate (workflow switch, module
  // rail), and the panel closes rather than outliving the page it filtered.
  useCloseOnNavigate(open, () => onOpenChange(false));

  // Persist tab preference per category
  const storageKey = `${config.category}-filter-mode`;

  // Destructured so the effect depends on the setter, not on the whole filter
  // object it happens to hang off.
  const { setActiveMode } = filters;

  useEffect(() => {
    localStorageAsync.getItem(storageKey).then((stored) => {
      if (stored === "simple" || stored === "advanced") {
        setActiveTab(stored);
        setActiveMode(stored);
      }
    });
  }, [storageKey, setActiveMode]);

  const handleTabChange = useCallback(
    (tab: string) => {
      const filterTab = tab as FilterTab;
      setActiveTab(filterTab);
      filters.setActiveMode(filterTab);
      localStorageAsync.setItem(storageKey, filterTab);
    },
    [storageKey, filters],
  );

  const tabBar = (
    <Box shrink border="bottom" paddingX="lg" paddingBottom="sm">
      {/* ToggleGroup takes an array value and is single-select by default; an
          empty next value is ignored so a tab is always selected. */}
      <ToggleGroup
        size="sm"
        value={[activeTab]}
        onValueChange={(next: string[]) => {
          const v = next[0];
          if (v) handleTabChange(v);
        }}
      >
        <ToggleGroupItem value="simple">
          <Text size="sm" weight="medium">
            Simple
          </Text>
        </ToggleGroupItem>
        <ToggleGroupItem value="advanced">
          <Text size="sm" weight="medium">
            Advanced
          </Text>
        </ToggleGroupItem>
      </ToggleGroup>
    </Box>
  );

  const activePanel =
    activeTab === "simple" ? (
      <QuickFilters config={config} filters={filters} />
    ) : (
      <AdvancedFilters config={config} filters={filters} />
    );

  const hasActiveFilters = filters.activeFilterCount > 0;
  const activeFilterSubtitle = hasActiveFilters
    ? `${filters.activeFilterCount} active`
    : undefined;

  const clearButton = hasActiveFilters ? (
    <Button variant="ghost" size="sm" onClick={filters.clearAll}>
      <Text size="sm" variant="muted">
        Clear
      </Text>
    </Button>
  ) : null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} snapPoints={MOBILE_SNAP_POINTS}>
        <DrawerPopup showBar>
          <DrawerHeader>
            <Box direction="row" align="center" justify="between" gap="sm">
              <Box direction="row" align="center" gap="sm">
                <DrawerTitle>Filters</DrawerTitle>
                {activeFilterSubtitle ? (
                  <Text size="sm" variant="muted">
                    {activeFilterSubtitle}
                  </Text>
                ) : null}
              </Box>
              {clearButton}
            </Box>
          </DrawerHeader>
          <DrawerPanel className="px-0 pt-3">
            {tabBar}
            {activePanel}
          </DrawerPanel>
        </DrawerPopup>
      </Drawer>
    );
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      modal={false}
      disablePointerDismissal
    >
      <SheetPopup
        className={ANCHORED_SHEET_POPUP_CLASS}
        showCloseButton={false}
        showBackdrop={false}
        style={{
          marginBottom: insets.bottom,
          marginRight: insets.right,
          marginTop: insets.top,
        }}
      >
        <Box
          direction="row"
          align="center"
          gap="sm"
          paddingX="md"
          paddingY="sm"
          shrink={false}
        >
          <Box direction="row" align="center" gap="sm" shrink fill="flex">
            <SheetTitle>Filters</SheetTitle>
            <FilterCountBadge count={filters.activeFilterCount} />
          </Box>
          <Box direction="row" align="center" gap="2xs" shrink={false}>
            {clearButton}
            <SheetClose
              render={<Button variant="ghost" size="icon-sm" aria-label="Close" />}
            >
              <Icon name="x" size="sm" />
            </SheetClose>
          </Box>
        </Box>
        {tabBar}
        <SheetPanel className="p-0">{activePanel}</SheetPanel>
      </SheetPopup>
    </Sheet>
  );
}
